import { z } from 'zod';
/**
 * Marca de la entidad: logo, color principal de sus pantallas (portal ciudadano y backoffice) y membrete (encabezado
 * y pie) de los documentos PDF que emite la entidad.
 */
/** Temas predefinidos. `XILTRION` deja los colores del sistema de diseño. */
export const BRAND_THEMES = [
    { code: 'XILTRION', label: 'Xiltrion (predeterminado)', primary: null },
    { code: 'AZUL', label: 'Azul institucional', primary: '#1d4ed8' },
    /** Cyan de proceso, el de varios logos institucionales, un paso más claro para que el texto oscuro de los botones (`readableOn`) contraste más. */
    { code: 'CYAN', label: 'Cyan', primary: '#00ccff' },
    { code: 'VERDE', label: 'Verde', primary: '#15803d' },
    { code: 'ROJO', label: 'Rojo', primary: '#b91c1c' },
    { code: 'NARANJA', label: 'Naranja', primary: '#c2410c' },
    { code: 'MORADO', label: 'Morado', primary: '#7e22ce' },
    { code: 'GRIS', label: 'Gris pizarra', primary: '#334155' },
];
export const BRAND_THEME_CODES = ['XILTRION', 'AZUL', 'CYAN', 'VERDE', 'ROJO', 'NARANJA', 'MORADO', 'GRIS', 'CUSTOM'];
export const HEX_COLOR = /^#[0-9a-f]{6}$/i;
export const brandColorsSchema = z
    .object({
    theme: z.enum(BRAND_THEME_CODES),
    customColor: z.string().regex(HEX_COLOR, 'Color en formato #RRGGBB').nullable().optional(),
})
    .refine((v) => v.theme !== 'CUSTOM' || !!v.customColor, { message: 'Elija el color personalizado', path: ['customColor'] });
/** Imágenes de la marca: el logo y las dos franjas del membrete. */
export const BRAND_IMAGE_SLOTS = ['LOGO', 'HEADER', 'FOOTER'];
export const BRAND_IMAGE_ACCEPT = 'image/png,image/jpeg,.png,.jpg,.jpeg';
/** Límite por imagen: el logo viaja al portal; las franjas se incrustan en cada PDF. */
export const BRAND_IMAGE_MAX_BYTES = { LOGO: 512 * 1024, HEADER: 1024 * 1024, FOOTER: 1024 * 1024 };
export const brandImageUploadSchema = z.object({
    fileName: z.string().min(1).max(200),
    /** PNG o JPEG en base64 (sin el prefijo data:). */
    base64: z.string().min(1).max(Math.ceil((1024 * 1024 * 4) / 3) + 8),
});
/**
 * Geometría del membrete en los PDF (puntos tipográficos, página carta de 612 × 792): las franjas van de borde a borde
 * dentro de `EDGE` y no pasan del alto máximo (si son más altas, se reducen manteniendo la proporción).
 */
export const LETTERHEAD_PAGE_PT = { width: 612, height: 792 };
export const LETTERHEAD_EDGE_PT = 28;
export const LETTERHEAD_BAND_MAX_PT = { header: 120, footer: 90 };
/**
 * Membrete tomado de un documento que la entidad ya usa: PDF, Word o una imagen de la hoja. La web dibuja la primera
 * página, detecta el encabezado y el pie, y los recorta; Word se convierte a PDF en el servidor.
 */
export const LETTERHEAD_SOURCE_ACCEPT = 'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,image/png,image/jpeg,.pdf,.docx,.doc,.png,.jpg,.jpeg';
export const LETTERHEAD_SOURCE_MAX_BYTES = 15 * 1024 * 1024;
export const LETTERHEAD_WORD_MIMES = ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
/** Word para convertir a PDF (el resto lo lee la web). */
export const letterheadSourceSchema = z.object({
    fileName: z.string().min(1).max(200),
    base64: z.string().min(1).max(Math.ceil((LETTERHEAD_SOURCE_MAX_BYTES * 4) / 3) + 8),
});
/** Encabezado y pie juntos (vista previa o guardar). `null`: sin esa franja. */
export const letterheadSchema = z.object({
    header: brandImageUploadSchema.nullable(),
    footer: brandImageUploadSchema.nullable(),
    /** Documento del que salieron, para la auditoría. */
    sourceFileName: z.string().max(200).optional(),
});
/** Color principal efectivo de un tema. */
export function brandPrimary(theme, customColor) {
    if (theme === 'CUSTOM')
        return customColor && HEX_COLOR.test(customColor) ? customColor.toLowerCase() : null;
    return BRAND_THEMES.find((t) => t.code === theme)?.primary ?? null;
}
/** Texto legible sobre un color: blanco o casi negro según su luminancia (WCAG). */
export function readableOn(hex) {
    const ch = (i) => {
        const c = parseInt(hex.slice(i, i + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const l = 0.2126 * ch(1) + 0.7152 * ch(3) + 0.0722 * ch(5);
    return (1.05 / (l + 0.05)) >= ((l + 0.05) / 0.05) ? '#ffffff' : '#111827';
}
/** Mezcla dos colores #RRGGBB en sRGB: `weight` es la parte de `a` (0..1). */
export function mixHex(a, b, weight) {
    const ch = (h, i) => parseInt(h.slice(i, i + 2), 16);
    const w = Math.min(1, Math.max(0, weight));
    return `#${[1, 3, 5].map((i) => Math.round(ch(a, i) * w + ch(b, i) * (1 - w)).toString(16).padStart(2, '0')).join('')}`;
}
/**
 * Tokens del tema de @xiltrion/ui que se derivan del color principal de la entidad, para ponerlos en `<html>` y que
 * todo lo que usa `bg-primary`, `text-primary`, `ring`, `bg-accent`… tome el color sin tocar componentes. En modo
 * oscuro el color se aclara un poco (como hace el tema con el suyo) y el acento se oscurece.
 */
export function brandTokens(primary, mode = 'light') {
    const p = mode === 'dark' ? mixHex(primary, '#ffffff', 0.8) : primary;
    return {
        '--primary': p,
        '--primary-foreground': readableOn(p),
        '--ring': mode === 'dark' ? p : mixHex(primary, '#ffffff', 0.7),
        '--accent': mode === 'dark' ? mixHex(primary, '#000000', 0.3) : mixHex(primary, '#ffffff', 0.1),
        '--accent-foreground': mode === 'dark' ? mixHex(primary, '#ffffff', 0.4) : mixHex(primary, '#000000', 0.75),
    };
}
export const BRAND_TOKENS = ['--primary', '--primary-foreground', '--ring', '--accent', '--accent-foreground'];
//# sourceMappingURL=branding.js.map